<template>
  <iColumn
    width="fill"
    gap="none"
    data-test="livestream-watch"
    :style-overrides="frameStyles"
  >
    <iColumn
      width="fill"
      gap="none"
      position="relative"
      overflow="hidden"
      :min-height="STAGE_MIN_HEIGHT"
      :style-overrides="stageStyles"
    >
      <iMediaStreamVideo
        v-if="videoTrack && !isIntermission"
        :track="videoTrack"
        object-fit="contain"
        border-radius="none"
        data-test="livestream-player"
      />

      <iColumn
        v-else
        width="fill"
        height="fill"
        position="relative"
        :min-height="STAGE_MIN_HEIGHT"
        :data-test="isIntermission ? 'livestream-intermission' : 'livestream-slate'"
      >
        <iImageContainer
          v-if="slateImage"
          :src="slateImage"
          width="fill"
          :height="STAGE_MIN_HEIGHT"
          crop-if-needed
          whitespace-style="blur"
          data-test="livestream-cover"
        />
        <iColumn
          width="fill"
          height="fill"
          align="center"
          vertical-align="middle"
          gap="standard"
          position="absolute"
          :position-placements="{top: '0', bottom: '0', left: '0', right: '0'}"
          :style-overrides="{backgroundColor: slateBackground}"
        >
          <iLoadingSpinner v-if="isPending" />
          <iRow
            v-if="countdownSegments && !isIntermission"
            gap="large"
            vertical-align="middle"
            wrap="nowrap"
            data-test="livestream-countdown"
          >
            <iStat
              v-if="countdownSegments.days > 0"
              :value="countdownSegments.days"
              label="days"
              size="large"
            />
            <iStat :value="countdownSegments.hours" label="hours" size="large" />
            <iStat :value="countdownSegments.minutes" label="minutes" size="large" />
            <iStat
              v-if="countdownSegments.days === 0"
              :value="countdownSegments.seconds"
              label="seconds"
              size="large"
            />
          </iRow>
          <iText font-size="large" font-weight="dark" :variant="slateImage ? 'light' : 'standard'">
            {{ slateTitle }}
          </iText>
          <iText font-size="small" :variant="slateImage ? 'light' : 'subtle'">
            {{ slateSubtitle }}
          </iText>
        </iColumn>
      </iColumn>

      <AudioTrackRenderer v-if="audioTrack" :audio-track="audioTrack" />

      <iColumn
        v-if="audioBlocked && videoTrack"
        width="fill"
        height="fill"
        align="center"
        vertical-align="middle"
        position="absolute"
        :position-placements="{top: '0', bottom: '0', left: '0', right: '0'}"
        :z-index="3"
      >
        <iButton
          variant="secondary"
          size="large"
          icon-left="ic:baseline-volume-up"
          data-test="livestream-unmute"
          @click="$emit('enable-audio')"
        >
          Turn on sound
        </iButton>
      </iColumn>

      <ReactionBurstLayer v-if="isLive" :bursts="reactionBursts" />
      <iRow
        v-if="isLive && canReact"
        class="livestream-reaction-bar"
        align="left"
        width="hug"
        data-test="livestream-reaction-bar"
      >
        <ReactionQuickBar @send="$emit('send-reaction', $event)" />
      </iRow>

      <iRow
        class="livestream-overlay"
        gap="small"
        align="left"
        vertical-align="middle"
        width="hug"
        wrap="nowrap"
      >
        <iBadge
          v-if="isLive && roomAppearance.showLiveBadge"
          label="LIVE"
          variant="striking"
          size="small"
          pulse
          data-test="livestream-live-chip"
        />
        <iStat
          v-if="isLive && roomAppearance.showViewerCount"
          :value="viewerCount"
          label="watching"
          size="small"
          abbreviate-number
          data-test="livestream-viewer-count"
        />
      </iRow>
    </iColumn>
  </iColumn>
</template>

<script>
import AudioTrackRenderer from "%/apps/video-engine/components/video-call/shared/AudioTrackRenderer.vue";
import ReactionBurstLayer from "%/apps/video-engine/components/video-call/stage/ReactionBurstLayer.vue";
import ReactionQuickBar from "%/apps/video-engine/components/video-call/controls/ReactionQuickBar.vue";
import { RoomAppearanceMixin } from "%/apps/video-engine/room-appearance";

// A livestream is not a call: viewers get a player, never the meeting stack
// (no lobby, no device prompts, no tiles).
const STAGE_MIN_HEIGHT = 360;

export default {
  name: "LivestreamWatchView",
  components: { AudioTrackRenderer, ReactionBurstLayer, ReactionQuickBar },
  mixins: [RoomAppearanceMixin],
  props: {
    coverImage: {
      type: String,
      required: false,
      default: "",
    },
    countdownSegments: {
      type: Object,
      required: false,
      default: null,
    },
    audioTrack: {
      type: Object,
      required: false,
      default: null,
    },
    audioBlocked: {
      type: Boolean,
      required: false,
      default: false,
    },
    videoTrack: {
      type: Object,
      required: false,
      default: null,
    },
    isLive: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPending: {
      type: Boolean,
      required: false,
      default: false,
    },
    statusTitle: {
      type: String,
      required: false,
      default: "",
    },
    statusSubtitle: {
      type: String,
      required: false,
      default: "",
    },
    reactionBursts: {
      type: Array,
      default: () => [],
    },
    watchState: {
      type: String,
      default: "waiting",
    },
    intermissionImage: {
      type: String,
      default: "",
    },
    canReact: {
      type: Boolean,
      default: false,
    },
    viewerCount: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ["enable-audio", "send-reaction"],
  data() {
    return {
      STAGE_MIN_HEIGHT,
    };
  },
  computed: {
    isIntermission() {
      return this.watchState === "intermission";
    },
    slateBackground() {
      if (this.slateImage) {
        return "dimmed";
      }

      return this.isIntermission ? "subtle" : "none";
    },
    slateImage() {
      if (this.isIntermission && this.intermissionImage) {
        return this.intermissionImage;
      }

      return this.coverImage;
    },
    slateTitle() {
      return this.statusTitle || "Waiting for the broadcaster";
    },
    slateSubtitle() {
      return this.statusSubtitle || "The stream will start automatically.";
    },
    frameStyles() {
      return {
        borderRadius: "large",
        // Four sides, never a combined `padding`: iColumn declares no such key
        // and VCL drops an invalid override silently.
        paddingTop: "none",
        paddingBottom: "none",
        paddingLeft: "none",
        paddingRight: "none",
      };
    },
    stageStyles() {
      return {
        borderRadius: "large",
      };
    },
  },
};
</script>

<style scoped lang="scss">
.livestream-reaction-bar {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 5;
}

.livestream-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  pointer-events: none;
}
</style>
